import { Injectable } from '@angular/core';

/**
 * Serviço de cores de itens educacionais (componentes curriculares).
 *
 * Paleta e nomes de classe idênticos ao AngularJS (cor-item-educacional.service.js).
 * Regra de atribuição: a cor é determinada pela posição do componente na lista
 * retornada pela API — igual ao comportamento do AngularJS.
 *
 * Para listas renderizadas via *ngFor use getColorByIndex(i).
 * Para lookups por ID (ex: frequência) use setContextComponentes() ao trocar
 * de contexto e depois getColor(id).
 */
@Injectable({
  providedIn: 'root'
})
export class ItemEducacionalColorService {
  private readonly CORES = [
    '#3498db', // blueA7
    '#e74c3c', // red7
    '#f39c12', // yellow7
    '#2ecc71', // greenA6
    '#9b59b6', // purpleA6
    '#8e44ad', // violetA6
    '#c0392b', // crimson6
    '#2980b9', // blueB10
    '#27ae60', // greenB6
    '#8e44ad', // purpleB6
    '#9b59b6', // violetA10
    '#f1c40f'  // yellow10
  ];

  private readonly CLASSE_PARA_HEX: Record<string, string> = {
    blueA7:    '#3498db',
    red7:      '#e74c3c',
    yellow7:   '#f39c12',
    greenA6:   '#2ecc71',
    purpleA6:  '#9b59b6',
    violetA6:  '#8e44ad',
    crimson6:  '#c0392b',
    blueB10:   '#2980b9',
    greenB6:   '#27ae60',
    purpleB6:  '#8e44ad',
    violetA10: '#9b59b6',
    yellow10:  '#f1c40f'
  };

  /**
   * Mapa persistente de cor por ID, garantindo cor fixa para cada componente curricular.
   * Nunca perde a cor de um ID já visto, mesmo se a lista for filtrada ou alterada.
   */
  private corPorId = new Map<number, string>();

  /**
   * Retorna a cor pelo índice posicional na paleta.
   * Use com o índice do *ngFor — sem cache, sempre determinístico.
   */
  getColorByIndex(index: number): string {
    return this.CORES[index % this.CORES.length];
  }

  /**
   * Converte nome de classe CSS do AngularJS (ex: "blueA7") para hex.
   * Retorna null se não reconhecido.
   */
  getColorFromClassName(className: string | null | undefined): string | null {
    if (!className) return null;
    return this.CLASSE_PARA_HEX[className] ?? null;
  }

  /**
   * Retorna a cor de um item educacional pelo ID.
   * Requer que setContextComponentes() tenha sido chamado antes com a lista atual.
   */
  getColor(id: number | null | undefined): string {
    if (!id) {
      return '#6b7280';
    }
    return this.corPorId.get(id) ?? '#6b7280';
  }

  /**
   * Registra os componentes do contexto atual (reset + preload na ordem da API).
   * Deve ser chamado toda vez que o contexto de turma/componentes mudar.
   * Garante que getColor(id) retorne a mesma cor que getColorByIndex(i).
   */
  setContextComponentes(componentes: Array<{ id: number; color?: string }>): void {
    this.corPorId.clear();
    componentes.forEach((comp, index) => {
      let cor = '';
      // Prioridade: hex do backend > nome de classe conhecido > paleta por índice
      if (comp.color && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(comp.color)) {
        cor = comp.color;
      } else if (comp.color && this.getColorFromClassName(comp.color)) {
        cor = this.getColorFromClassName(comp.color)!;
      } else {
        cor = this.CORES[index % this.CORES.length];
      }
      this.corPorId.set(comp.id, cor);
    });
  }
}
